import axios from 'axios';
import config from '../../config';
import { Eventbus } from '../../utils';
import { events } from '../../utils/constants';
import logger from '../../utils/logger';
import ToastService from '../toast.service';
import { AuthenticationService } from '../authentication.service';
import BookingApi from './booking.api';
import ConfigApi from './config.api';
import EnquiryApi from './enquiry.api';
import EnrolmentApi from './enrolment.api';
import WaitlistApi from './waitlist.api';
import CentreApi from './centre.api';
import EntraApi from './entra.api';
import CommunicationApi from './communication.api';
import PublicDataApi from './publicdata.api';
import AddressPickerApi from './addresspicker.api';
import PresenceApi from './presence.api';
import DashboardApi from './dashboard.api';

const axiosInstance = axios.create();
axiosInstance.defaults.headers.common['content-type'] = 'application/json';
axiosInstance.defaults.baseURL = config.pathwayApiUrl;
axiosInstance.defaults.withCredentials = true;

const ongoingRequests = new Set();

function isUnsafeHttpMethod(method) {
    return ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());
}

function serializeRequestPart(value) {
    if (!value) return '';
    if (value instanceof URLSearchParams) return value.toString();
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function getRequestKey(request) {
    return [
        request.method,
        request.url,
        serializeRequestPart(request.params),
        serializeRequestPart(request.data)
    ].join('-');
}

async function recoverUnauthorizedRequest(error) {
    return AuthenticationService.recoverUnauthorizedRequest({
        error,
        retryRequest: (requestConfig) => axiosInstance(requestConfig),
        shouldRefreshCsrf: isUnsafeHttpMethod,
        setCsrfHeader: (requestConfig, csrfToken) => {
            requestConfig.headers = {
                ...(requestConfig.headers || {}),
                'X-CSRF-TOKEN': csrfToken
            };
        },
        loginReturnUrl: `${window.location.origin}/login`
    });
}

// Request interceptor
axiosInstance.interceptors.request.use(async request => {
    const shouldTrackRequest = isUnsafeHttpMethod(request.method);
    const requestKey = shouldTrackRequest ? getRequestKey(request) : null;
    if (shouldTrackRequest && ongoingRequests.has(requestKey)) {
        const error = new axios.Cancel('Duplicate request cancelled');
        return Promise.reject(error);
    }
    if (shouldTrackRequest) {
        ongoingRequests.add(requestKey);
    }

    let isAuthenticated = AuthenticationService.isAuthenticated();
    if (!isAuthenticated) {
        await AuthenticationService.refreshToken();
        isAuthenticated = AuthenticationService.isAuthenticated();
    }
    if (!isAuthenticated) {
        if (shouldTrackRequest) {
            ongoingRequests.delete(requestKey);
        }
        return Promise.reject(new axios.Cancel('User not authenticated'));
    }

    request.withCredentials = true;

    if (isUnsafeHttpMethod(request.method)) {
        const csrfToken = await AuthenticationService.getCsrfToken();
        if (csrfToken) {
            request.headers['X-CSRF-TOKEN'] = csrfToken;
        }
    }

    return request;
}, function (error) {
    if (error.config) {
        const requestKey = getRequestKey(error.config);
        ongoingRequests.delete(requestKey);
    }
    return Promise.reject(error);
});

// Response interceptor
axiosInstance.interceptors.response.use((response) => {
    const requestKey = getRequestKey(response.config);
    ongoingRequests.delete(requestKey);

    if (response.status >= 400) {
        const error = new Error(`HTTP Error ${response.status}`);
        error.response = response;
        error.config = response.config;
        return Promise.reject(error);
    }

    return response?.data?.data || response?.data || response;
}, (error) => {
    if (error && error.config) {
        const requestKey = getRequestKey(error.config);
        ongoingRequests.delete(requestKey);
    }

    if (error?.response?.status === 401) {
        return recoverUnauthorizedRequest(error).then((recoveredResponse) => {
            if (recoveredResponse) return recoveredResponse;

            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
            return Promise.reject(error);
        });
    }

    if (error?.response && error.config?.ignoreStatuses?.includes(error.response.status)) {
        return Promise.reject(error);
    }

    if (error && error.response) {
        const status = error.response.status;
        if (status === 403) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }

        const responseData = error.response.data || {};
        const title = responseData.statusCode || responseData.title || (status === 401 || status === 403 ? 'Unauthorized' : `Error ${status}`);
        const errors = responseData.errors || responseData.message || 'An error occurred';

        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;

        const showToast = error.config?.suppressErrorToast !== true;
        if (showToast && title && errors) {
            ToastService.displayToast({
                type: 'error',
                title: title,
                data: errors,
                toastId: toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }

        logger.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: status,
            title: title,
            errors: errors
        });

        try {
            error.title = title;
            error.errors = errors;
            error.toastShown = showToast;
            error.loggedCentrally = true;
        } catch (e) {
            // ignore if we cannot assign
        }
    } else if (error && error.code === 'ERR_NETWORK') {
        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const usesCookieSession = error.config?.withCredentials === true;
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;

        if (usesCookieSession) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }

        const showToast = error.config?.suppressErrorToast !== true;
        if (showToast) {
            const errorMessage = usesCookieSession
                ? 'Access denied or authorization failed. You may not have permission for this action.'
                : 'Unable to reach the server. Please check your connection.';

            ToastService.displayToast({
                type: 'error',
                title: 'Request Failed',
                data: errorMessage,
                toastId: toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }

        logger.error('Network Error:', {
            url: url,
            method: method,
            message: error.message,
            code: error.code,
            usesCookieSession: usesCookieSession,
        });
    }

    return Promise.reject(error);
});

function getDataResponse(response) {
    return response.data !== undefined ? response.data : response;
}

function buildRequest(overrideInstance) {
    return Promise.resolve(overrideInstance || axiosInstance);
}

function makeRequest(req) {
    return req.then(getDataResponse).catch((error) => {
        if (axios.isCancel(error)) {
            return Promise.resolve({ code: 'ERR_CANCELED' });
        }
        return Promise.reject(error);
    });
}

const api = {
	...BookingApi({ makeRequest, buildRequest }),
    ...ConfigApi({ makeRequest, buildRequest }),
    ...EnquiryApi({ makeRequest, buildRequest }),
    ...EnrolmentApi({ makeRequest, buildRequest }),
    ...WaitlistApi({ makeRequest, buildRequest }),
    ...CentreApi({ makeRequest, buildRequest }),
    ...EntraApi({ makeRequest, buildRequest }),
    ...CommunicationApi({ makeRequest, buildRequest }),
    ...PublicDataApi({ makeRequest, buildRequest }),
    ...AddressPickerApi({ makeRequest, buildRequest }),
    ...PresenceApi({ makeRequest, buildRequest }),
    ...DashboardApi({ makeRequest, buildRequest }),
};

export default api;
export { axiosInstance };
